import type {
  Account,
  ComprehensiveSettings,
  Document,
  GetDocumentsFolders200Item,
  MarketAsset,
  PersonalInfo,
  Preferences,
  SecuritySettings,
  TaxSettings,
  Transaction,
  Vehicle,
} from '@/api/generated/model';
import {
  deleteAuthAccount,
  deleteAuthSessions,
  deleteAuthSessionsSessionId,
  getAuthSessions,
  getTransactions,
  postAuthLogin,
  putAuthPassword,
} from './generated/fintecyWealthManagerAPI';
import type { LoginRequest, LoginResponse } from './generated/model';

// Mock data imports
import marketData from './mock/market.json';
import priceHistoryData from './mock/price-history.json';
import settingsData from './mock/settings.json';

// Generated API client imports
import {
  deleteAccountsId,
  getAccounts,
  postAccounts,
  postAccountsIdRefresh,
  putAccountsId,
} from './generated/fintecyWealthManagerAPI';

// Add vehicle-related imports
import { deleteVehiclesId, getVehicles, putVehiclesId } from './generated/fintecyWealthManagerAPI';

// Add comprehensive settings import
import { getSettings } from './generated/fintecyWealthManagerAPI';

// Add vehicle-related type imports
import { STORAGE_KEYS, StoredUserInfo } from '@/api/services/googleAuthService.ts';
import type {
  PutVehiclesIdBody,
  PutVehiclesIdBodyStatus,
  PutVehiclesIdBodyType,
} from './generated/model';

const PREFERENCES_STORAGE_KEY = 'app_preferences';
const TOKEN_KEY = 'auth_token';
const VEHICLES_CACHE_KEY = 'vehicles';
const CURRENT_VEHICLE_ID_KEY = 'currentVehicleId';

type Session = {
  id: string;
  device: string;
  location: string;
  lastActive: string;
  isCurrent: boolean;
};

export const authService = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await postAuthLogin(credentials);
    if (response.token) {
      this.setToken(response.token);
    }
    return response;
  },

  async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
    await putAuthPassword({ currentPassword, newPassword });
  },

  setToken(token: string) {
    localStorage.setItem(TOKEN_KEY, token);
  },

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  removeToken() {
    localStorage.removeItem(TOKEN_KEY);
  },

  isAuthenticated(): boolean {
    return !!this.getToken();
  },

  async getSessions(): Promise<Session[]> {
    const sessions = await getAuthSessions();
    return sessions as Session[];
  },

  async revokeSession(sessionId: string): Promise<void> {
    await deleteAuthSessionsSessionId(sessionId);
  },

  async revokeAllSessions(): Promise<void> {
    await deleteAuthSessions();
  },

  async deleteAccount(): Promise<void> {
    await deleteAuthAccount();
    this.removeToken();
  },
};

// Simulated API delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Accounts Service
export const accountsService = {
  async getAccounts(): Promise<Account[]> {
    return await getAccounts();
  },

  async createAccount(account: Omit<Account, 'id'>): Promise<Account> {
    return await postAccounts(account);
  },

  async updateAccount(id: string, account: Partial<Omit<Account, 'id'>>): Promise<Account> {
    return await putAccountsId(id, account);
  },

  async deleteAccount(id: string): Promise<void> {
    await deleteAccountsId(id);
  },

  async refreshAccountBalance(id: string): Promise<Account> {
    return await postAccountsIdRefresh(id);
  },
};

// Transactions Service
export const transactionsService = {
  async getTransactions(): Promise<Transaction[]> {
    return await getTransactions();
    // await delay(500);
    // return dashboardData.recentTransactions as Transaction[];
  },

  async createTransaction(transaction: Omit<Transaction, 'id'>): Promise<Transaction> {
    // For now, simulate API call with delay and generate ID
    await delay(500);
    const newTransaction: Transaction = {
      ...transaction,
      id: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };

    // In a real app, this would make an API call to create the transaction
    // For now, we'll just return the transaction with an ID
    return newTransaction;
  },
};

// Market Service
export const marketService = {
  async getMarketAssets(type?: 'stock' | 'crypto' | 'forex'): Promise<MarketAsset[]> {
    await delay(500);
    const assets = marketData.assets as MarketAsset[];
    if (!type) return assets;
    return assets.filter((asset) => asset.type === type);
  },
  async getPriceHistory(assetId: string): Promise<Array<{ name: string; price: number }>> {
    await delay(500);
    // In a real app, this would be filtered by assetId
    return priceHistoryData.priceHistory;
  },
};

// Settings Service
export const settingsService = {
  async getPersonalInfo(): Promise<PersonalInfo> {
    try {
      const settings = await getSettings();
      if (settings.personalInfo) {
        // Convert comprehensive personal info to legacy format
        return {
          firstName: settings.personalInfo.firstName || '',
          lastName: settings.personalInfo.lastName || '',
          email: settings.personalInfo.email || '',
          phone: settings.personalInfo.phone || '',
          address: settings.personalInfo.address || '',
          dateOfBirth: settings.personalInfo.dateOfBirth || '',
        } as PersonalInfo;
      }
      // Fallback to mock data if no personal info in settings
      const stored = localStorage.getItem(STORAGE_KEYS.USER_INFO);
      if (stored) {
        const userInfo = JSON.parse(stored) as StoredUserInfo;
        return {
          ...settingsData.personalInfo,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          email: userInfo.email,
        } as PersonalInfo;
      } else {
        return settingsData.personalInfo as PersonalInfo;
      }
    } catch (error) {
      console.error('Error fetching personal info:', error);
      // Fallback to mock data on error
      await delay(500);
      const stored = localStorage.getItem(STORAGE_KEYS.USER_INFO);
      if (stored) {
        const userInfo = JSON.parse(stored) as StoredUserInfo;
        return {
          ...settingsData.personalInfo,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          email: userInfo.email,
        } as PersonalInfo;
      } else {
        return settingsData.personalInfo as PersonalInfo;
      }
    }
  },

  async updatePersonalInfo(info: PersonalInfo): Promise<PersonalInfo> {
    await delay(500);
    return info;
  },

  async getTaxSettings(): Promise<TaxSettings> {
    try {
      const settings = await getSettings();
      if (settings.taxSettings) {
        // Convert comprehensive tax settings to legacy format
        return {
          taxId: settings.taxSettings.taxId || '',
          filingStatus:
            (settings.taxSettings.filingStatus as 'single' | 'married' | 'head_of_household') ||
            'single',
          taxBracket: settings.taxSettings.taxBracket || '',
          deductions: settings.taxSettings.deductions || [],
          exemptions: settings.taxSettings.exemptions || 0,
        } as TaxSettings;
      }
      // Fallback to mock data
      await delay(500);
      return settingsData.taxSettings as TaxSettings;
    } catch (error) {
      console.error('Error fetching tax settings:', error);
      // Fallback to mock data on error
      await delay(500);
      return settingsData.taxSettings as TaxSettings;
    }
  },

  async updateTaxSettings(settings: TaxSettings): Promise<TaxSettings> {
    await delay(500);
    return settings;
  },

  async getPreferences(): Promise<Preferences> {
    try {
      const settings = await getSettings();
      if (settings.preferences) {
        // Convert comprehensive preferences to legacy format
        return {
          theme: (settings.preferences.theme as 'light' | 'dark' | 'system') || 'system',
          currency: settings.preferences.currency || 'USD',
          dateFormat: 'mm/dd/yyyy', // Default fallback
          language: settings.preferences.language || 'en',
          notifications: {
            email: settings.preferences.notifications?.email ?? true,
            balance: true, // Default fallback
            investment: true, // Default fallback
            reports: settings.preferences.notifications?.push ?? true,
          },
        } as Preferences;
      }
      // Fallback to mock data
      await delay(500);
      return settingsData.preferences as unknown as Preferences;
    } catch (error) {
      console.error('Error fetching preferences:', error);
      // Fallback to mock data on error
      await delay(500);
      return settingsData.preferences as unknown as Preferences;
    }
  },

  async updatePreferences(preferences: Preferences): Promise<Preferences> {
    await delay(500);
    return preferences;
  },

  async getSecuritySettings(): Promise<SecuritySettings> {
    try {
      const settings = await getSettings();
      if (settings.securitySettings) {
        // Convert comprehensive security settings to legacy format
        return {
          twoFactorEnabled: settings.securitySettings.twoFactorEnabled ?? false,
          biometricEnabled: settings.securitySettings.biometricEnabled ?? false,
          lastPasswordChange:
            settings.securitySettings.lastPasswordChange || new Date().toISOString(),
          securityQuestions: settings.securitySettings.securityQuestions || [],
          trustedDevices: settings.securitySettings.trustedDevices || [],
        } as SecuritySettings;
      }
      // Fallback to mock data
      await delay(500);
      return settingsData.securitySettings as SecuritySettings;
    } catch (error) {
      console.error('Error fetching security settings:', error);
      // Fallback to mock data on error
      await delay(500);
      return settingsData.securitySettings as SecuritySettings;
    }
  },

  async updateSecuritySettings(settings: SecuritySettings): Promise<SecuritySettings> {
    await delay(500);
    return settings;
  },

  async getAllSettings(): Promise<ComprehensiveSettings> {
    try {
      return await getSettings();
    } catch (error) {
      console.error('Error fetching comprehensive settings:', error);
      // Fallback to mock data on error
      await delay(500);
      return settingsData as ComprehensiveSettings;
    }
  },

  async getCountries(): Promise<Array<{ code: string; name: string }>> {
    await delay(500);
    return [
      { code: 'US', name: 'United States' },
      { code: 'CA', name: 'Canada' },
      { code: 'GB', name: 'United Kingdom' },
      { code: 'AU', name: 'Australia' },
      { code: 'DE', name: 'Germany' },
      { code: 'FR', name: 'France' },
      { code: 'JP', name: 'Japan' },
      { code: 'CN', name: 'China' },
    ];
  },

  async getFilingStatuses(): Promise<Array<{ value: string; label: string }>> {
    await delay(500);
    return [
      { value: 'single', label: 'Single' },
      { value: 'married_joint', label: 'Married Filing Jointly' },
      { value: 'married_separate', label: 'Married Filing Separately' },
      { value: 'head_household', label: 'Head of Household' },
      { value: 'qualifying_widow', label: 'Qualifying Widow(er)' },
    ];
  },
};

export const preferencesService = {
  async fetchPreferences(): Promise<Preferences> {
    return getSettings().then((value) => value.preferences);
  },

  async updatePreferences(preferences: Preferences): Promise<Preferences> {
    // In a real app, this would be an API call
    // For now, we'll just simulate a successful update
    this.savePreferencesToStorage(preferences);
    return preferences;
  },

  savePreferencesToStorage(preferences: Preferences): void {
    localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
  },

  getPreferencesFromStorage(): Preferences | null {
    const stored = localStorage.getItem(PREFERENCES_STORAGE_KEY);
    if (!stored) return null;

    const preferences = JSON.parse(stored);
    return {
      ...preferences,
      theme: preferences.theme as 'light' | 'dark' | 'system',
      language: preferences.language || 'en',
    };
  },

  clearPreferencesFromStorage(): void {
    localStorage.removeItem(PREFERENCES_STORAGE_KEY);
  },
};

// Vehicle Service
export const vehicleService = {
  async getVehicles(): Promise<Vehicle[]> {
    try {
      // Try to get from cache first
      const cachedVehicles = localStorage.getItem(VEHICLES_CACHE_KEY);
      if (cachedVehicles) {
        return JSON.parse(cachedVehicles);
      }

      // If not in cache, fetch from API
      const vehicles = await getVehicles();

      // Cache the result
      localStorage.setItem(VEHICLES_CACHE_KEY, JSON.stringify(vehicles));

      return vehicles;
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      throw error;
    }
  },

  getCurrentVehicleId(): string | null {
    return localStorage.getItem(CURRENT_VEHICLE_ID_KEY);
  },

  setCurrentVehicleId(id: string): void {
    localStorage.setItem(CURRENT_VEHICLE_ID_KEY, id);
  },

  async updateVehicle(id: string, data: Partial<Vehicle>): Promise<Vehicle> {
    try {
      // Convert Vehicle properties to PutVehiclesIdBody
      const updateData: PutVehiclesIdBody = {};

      if (data.name !== undefined) {
        updateData.name = data.name;
      }

      if (data.status !== undefined) {
        updateData.status = data.status as PutVehiclesIdBodyStatus;
      }

      if (data.type !== undefined) {
        // Convert VehicleType to PutVehiclesIdBodyType
        if (data.type === 'ltd') {
          updateData.type = 'legal_entity';
        } else {
          updateData.type = data.type as PutVehiclesIdBodyType;
        }
      }

      const updatedVehicle = await putVehiclesId(id, updateData);

      // Update cache
      const cachedVehicles = localStorage.getItem(VEHICLES_CACHE_KEY);
      if (cachedVehicles) {
        const vehicles = JSON.parse(cachedVehicles);
        const updatedVehicles = vehicles.map((v: Vehicle) => (v.id === id ? updatedVehicle : v));
        localStorage.setItem(VEHICLES_CACHE_KEY, JSON.stringify(updatedVehicles));
      }

      return updatedVehicle;
    } catch (error) {
      console.error('Error updating vehicle:', error);
      throw error;
    }
  },

  async deleteVehicle(id: string): Promise<void> {
    try {
      await deleteVehiclesId(id);

      // Update cache
      const cachedVehicles = localStorage.getItem(VEHICLES_CACHE_KEY);
      if (cachedVehicles) {
        const vehicles = JSON.parse(cachedVehicles);
        const updatedVehicles = vehicles.filter((v: Vehicle) => v.id !== id);
        localStorage.setItem(VEHICLES_CACHE_KEY, JSON.stringify(updatedVehicles));
      }

      // If we're deleting the current vehicle, set another one as current
      const currentVehicleId = this.getCurrentVehicleId();
      if (currentVehicleId === id) {
        const cachedVehicles = localStorage.getItem(VEHICLES_CACHE_KEY);
        if (cachedVehicles) {
          const vehicles = JSON.parse(cachedVehicles);
          if (vehicles.length > 0) {
            this.setCurrentVehicleId(vehicles[0].id);
          } else {
            localStorage.removeItem(CURRENT_VEHICLE_ID_KEY);
          }
        } else {
          localStorage.removeItem(CURRENT_VEHICLE_ID_KEY);
        }
      }
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      throw error;
    }
  },
};

// Vault Service
export const vaultService = {
  async createFolder(
    folder: Omit<GetDocumentsFolders200Item, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<GetDocumentsFolders200Item> {
    // For now, simulate API call with delay and generate ID
    await delay(500);
    const newFolder: GetDocumentsFolders200Item = {
      ...folder,
      id: Date.now(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // In a real app, this would make an API call to create the folder
    // For now, we'll just return the folder with an ID
    return newFolder;
  },

  async uploadDocument(document: Omit<Document, 'id'>, file: File): Promise<Document> {
    // For now, simulate API call with delay and generate ID
    await delay(1000); // Longer delay to simulate file upload
    const newDocument: Document = {
      ...document,
      id: `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };

    // In a real app, this would upload the file to a storage service
    // and make an API call to create the document record
    console.log('Uploading file:', file.name, 'Size:', file.size);

    return newDocument;
  },
};
